import { useState } from "react";
import { ArrowUpRight, BookOpen, Play, Maximize2, X } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Reveal } from "./Reveal";
import { trackEvent } from "@/lib/analytics";

const SABBATH_IMG = "https://customer-assets.emergentagent.com/job_sabbath-way/artifacts/wgipw0nn_Sabbath%20change.png";
const FEATURED_IMG = "https://images.unsplash.com/photo-1639761045652-2c8a36f32b3e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const FEATURED_URL = "https://www.history.com/articles/christmas-history-facts-trivia";

const articles = [
  {
    title: "History of Easter",
    desc: "A look at the origins of the holiest day on the Christian calendar.",
    url: "https://www.history.com/videos/history-of-easter",
    img: "https://images.unsplash.com/photo-1520283451192-c3b05d7db25b?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    kind: "History.com · Video",
    video: true,
  },
];

export const History = () => {
  const [open, setOpen] = useState(false);

  return (
    <section id="history" data-testid="history-section" className="bg-[#0A0A0A] py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#D4AF37] mb-4">
            Search The Origins
          </p>
          <h2 className="font-heading font-light tracking-tight text-4xl md:text-6xl max-w-4xl leading-tight mb-6">
            Know the <span className="italic text-[#D4AF37]">history</span> behind the traditions.
          </h2>
          <p className="text-base md:text-lg leading-relaxed font-light text-[#A39E98] max-w-2xl mb-12">
            Test everything against the truth. Examine where the customs of men
            came from — and compare them to what Scripture actually instructs.
          </p>
        </Reveal>

        {/* Sabbath Shift timeline image */}
        <Reveal delay={0.05}>
          <button
            data-testid="history-sabbath-trigger"
            onClick={() => setOpen(true)}
            className="group relative block w-full overflow-hidden border border-[#2B2825] mb-8"
          >
            <img
              src={SABBATH_IMG}
              alt="The Roman Empire Timeline of the Sabbath Shift"
              className="w-full h-auto transition-transform duration-700 group-hover:scale-[1.02]"
            />
            <div className="absolute inset-0 bg-[#0A0A0A]/0 group-hover:bg-[#0A0A0A]/20 transition-colors" />
            <span className="absolute bottom-4 right-4 flex items-center gap-2 border border-[#D4AF37] bg-[#0A0A0A]/70 backdrop-blur-sm text-[#D4AF37] px-4 py-2 text-xs tracking-wide uppercase font-mono opacity-90 group-hover:opacity-100 transition-opacity">
              <Maximize2 strokeWidth={1.5} className="h-4 w-4" />
              Expand
            </span>
          </button>
        </Reveal>

        {/* YouTube podcast embed */}
        <Reveal delay={0.08}>
          <div data-testid="history-podcast-embed" className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Play strokeWidth={1.5} className="h-4 w-4 text-[#D4AF37]" fill="currentColor" />
              <span className="font-mono text-xs tracking-[0.2em] uppercase text-[#D4AF37]">
                Podcast · Watch
              </span>
            </div>
            <h3 className="font-heading text-2xl md:text-3xl font-light text-[#F4F0EA] leading-tight mb-5">
              What Does History Prove?
            </h3>
            <div className="relative w-full overflow-hidden border border-[#2B2825]" style={{ paddingTop: "56.25%" }}>
              <iframe
                className="absolute inset-0 h-full w-full"
                src="https://www.youtube-nocookie.com/embed/t3Sf8rgAsuo?si=MhDcHmbU6_totbOs"
                title="What Does History Prove?"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          </div>
        </Reveal>

        {/* Featured card */}
      <Reveal delay={0.1}>
        <a
          href={FEATURED_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="history-article-link"
          onClick={() => trackEvent("click_external_link", { label: "History of Christmas", link_url: FEATURED_URL })}
          className="group grid grid-cols-1 lg:grid-cols-2 border border-[#2B2825] overflow-hidden hover:border-[#D4AF37]/60 transition-colors"
        >
          <div className="relative h-64 lg:h-auto overflow-hidden">
            <img
              src={FEATURED_IMG}
              alt="The history behind winter traditions"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/20 to-transparent" />
          </div>

          <div className="p-8 md:p-12 flex flex-col justify-center bg-[#141311]">
            <div className="flex items-center gap-2 mb-5">
              <BookOpen strokeWidth={1.5} className="h-4 w-4 text-[#D4AF37]" />
              <span className="font-mono text-xs tracking-[0.2em] uppercase text-[#D4AF37]">
                History.com · Article
              </span>
            </div>
            <h3 className="font-heading text-3xl md:text-4xl font-light text-[#F4F0EA] leading-tight mb-4">
              The History of Christmas
            </h3>
            <p className="text-base leading-relaxed font-light text-[#A39E98] mb-8">
              Facts, trivia, and the ancient roots of a modern tradition. Read
              the full account and weigh it for yourself.
            </p>
            <span className="inline-flex items-center gap-2 text-[#F4F0EA] font-medium group-hover:text-[#D4AF37] transition-colors">
              Read on History.com
              <ArrowUpRight
                strokeWidth={1.5}
                className="h-5 w-5 text-[#D4AF37] transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
              />
            </span>
          </div>
        </a>
      </Reveal>

      {/* Smaller grid of more origins */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {articles.map((a, i) => (
          <Reveal key={a.url} delay={0.15 + i * 0.08}>
            <a
              href={a.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`history-card-${i}`}
              onClick={() => trackEvent("click_external_link", { label: a.title, link_url: a.url })}
              className="group flex flex-col h-full border border-[#2B2825] overflow-hidden hover:border-[#D4AF37]/60 transition-colors"
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={a.img}
                  alt={a.title}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-[#0A0A0A]/30 group-hover:bg-[#0A0A0A]/15 transition-colors" />
                {a.video && (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="flex h-14 w-14 items-center justify-center border border-[#D4AF37] bg-[#0A0A0A]/50 backdrop-blur-sm text-[#D4AF37]">
                      <Play strokeWidth={1} className="h-6 w-6 translate-x-0.5" fill="currentColor" />
                    </span>
                  </span>
                )}
              </div>
              <div className="p-6 md:p-8 flex flex-col flex-1 bg-[#141311]">
                <span className="font-mono text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-3">
                  {a.kind}
                </span>
                <h3 className="font-heading text-2xl font-light text-[#F4F0EA] leading-tight mb-3">
                  {a.title}
                </h3>
                <p className="text-sm leading-relaxed font-light text-[#A39E98] mb-6">
                  {a.desc}
                </p>
                <span className="mt-auto inline-flex items-center gap-2 text-sm text-[#F4F0EA] font-medium group-hover:text-[#D4AF37] transition-colors">
                  {a.video ? "Watch on History.com" : "Read on History.com"}
                  <ArrowUpRight
                    strokeWidth={1.5}
                    className="h-4 w-4 text-[#D4AF37] transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </span>
              </div>
            </a>
          </Reveal>
        ))}
      </div>
      </div>

      <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
          <DialogPrimitive.Content
            data-testid="history-sabbath-modal"
            className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-10 focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0"
          >
            <DialogPrimitive.Title className="sr-only">The Roman Empire Timeline of the Sabbath Shift</DialogPrimitive.Title>
            <DialogPrimitive.Description className="sr-only">
              Full-size timeline image of the Sabbath shift under the Roman Empire.
            </DialogPrimitive.Description>
            <DialogPrimitive.Close
              data-testid="history-sabbath-close"
              className="fixed top-6 right-6 z-10 flex items-center gap-2 text-[#A39E98] hover:text-[#F4F0EA] transition-colors"
            >
              <span className="text-sm tracking-wide">Close</span>
              <X strokeWidth={1} className="h-5 w-5" />
            </DialogPrimitive.Close>
            <img
              src={SABBATH_IMG}
              alt="The Roman Empire Timeline of the Sabbath Shift"
              className="max-h-full max-w-full object-contain border border-[#4A453F]"
            />
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </section>
  );
};
